const DEFAULT_LOCALE = 'es-AR';

export const DATE_FORMATS = {
  SHORT: { year: 'numeric', month: '2-digit', day: '2-digit' },
  DATE_TIME_MEDIUM: { dateStyle: 'short', timeStyle: 'medium' },
  MONTH_YEAR: { month: '2-digit', year: 'numeric' },
};

const normalizeDecimals = (value, defaultValue = 0) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed) || parsed < 0) return defaultValue;
  const clamped = Math.min(Math.max(Math.floor(parsed), 0), 20);
  return clamped;
};

const currencyFormatter = (decimals = 0) =>
  new Intl.NumberFormat(DEFAULT_LOCALE, {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: normalizeDecimals(decimals, 0),
    maximumFractionDigits: normalizeDecimals(decimals, 0),
  });

const integerFormatter = new Intl.NumberFormat(DEFAULT_LOCALE, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const decimalFormatter = (decimals = 2) =>
  new Intl.NumberFormat(DEFAULT_LOCALE, {
    minimumFractionDigits: normalizeDecimals(decimals, 2),
    maximumFractionDigits: normalizeDecimals(decimals, 2),
  });

const shouldNormalizeToLocalTimezone = (value) => {
  if (typeof value !== 'string') return false;
  const trimmedValue = value.trim();
  if (!trimmedValue) return false;

  if (/[+\-]\d{2}:?\d{2}$/i.test(trimmedValue) || /z$/i.test(trimmedValue)) {
    return false;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
    return true;
  }

  if (/\b(GMT|UTC)\b/i.test(trimmedValue)) {
    return true;
  }

  return false;
};

const parseDateInput = (dateInput) => {
  const date =
    dateInput instanceof Date ? new Date(dateInput.getTime()) : new Date(dateInput);

  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }

  if (shouldNormalizeToLocalTimezone(dateInput)) {
    const offsetMilliseconds = date.getTimezoneOffset() * 60 * 1000;
    return new Date(date.getTime() + offsetMilliseconds);
  }

  return date;
};

const coerceNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = typeof value === 'string' ? Number.parseFloat(value) : Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

export const formatCurrency = (value, decimals = 0, { fallback = '$ 0,00' } = {}) => {
  const numeric = coerceNumber(value);
  return numeric === null ? fallback : currencyFormatter(decimals).format(numeric);
};

export const formatNumber = (value, { decimals = 2, fallback = '0' } = {}) => {
  const numeric = coerceNumber(value);
  if (numeric === null) return fallback;
  if (decimals === 0) return integerFormatter.format(numeric);
  return decimalFormatter(decimals).format(numeric);
};

export const formatPercentage = (value, { decimals = 2, fallback = '0%' } = {}) => {
  const numeric = coerceNumber(value);
  if (numeric === null) return fallback;
  return `${decimalFormatter(decimals).format(numeric)}%`;
};

export const formatDate = (
  dateInput,
  {
    locale = DEFAULT_LOCALE,
    options = DATE_FORMATS.SHORT,
    fallback = '-',
  } = {}
) => {
  if (!dateInput) return fallback;

  const date = parseDateInput(dateInput);
  if (!date) {
    return fallback;
  }

  return new Intl.DateTimeFormat(locale, options).format(date);
};

export const formatDateTime = (
  dateInput,
  { locale = DEFAULT_LOCALE, options = DATE_FORMATS.DATE_TIME_MEDIUM, fallback = '-' } = {}
) => formatDate(dateInput, { locale, options, fallback });

export const formatInvoiceNumber = (typeLetter, salePointNumber, receiptNumber) => {
  const point = String(salePointNumber ?? '').padStart(4, '0');
  const receipt = String(receiptNumber ?? '').padStart(8, '0');
  return `${typeLetter ?? ''}-${point}-${receipt}`;
};
